<template>
  <vs-wrapper>
    <v-row align="center" justify="center">
      <v-col class="grow heading-wrapper">
        <vs-heading tag="span" type="overline">Line items</vs-heading>
      </v-col>
      <v-col class="shrink">
        <DisabledActionTooltip
          v-if="showActionButton"
          :permission="lineItemPermission"
          :label="`${lineItemButtonLabel} line items`"
        >
          <template #default="{ disabled }">
            <vs-button
              data-test="edit-line-items-button"
              :label="lineItemButtonLabel"
              :icon="lineItemButtonIcon"
              :disabled="disabled"
              type="secondary"
              small
              @click="openLineItemBottomSheet"
            />
          </template>
        </DisabledActionTooltip>
      </v-col>
    </v-row>
    <vs-text
      v-if="currentOrgHasInvoicingEntitlement && showBatchLink && batch"
      type="subdued"
      style="margin-top: -12px"
      >Batch
      <router-link :to="{ name: batchRouteName, params: { batchId } }"
        >#{{ batch.invoiceNumber }}
      </router-link>
    </vs-text>

    <vs-wrapper v-if="isError" space="small">
      <template v-if="lineItemError?.status == 403">
        <vs-text data-test="no-permission-caption" type="caption"
          >You don't have permission to view this section</vs-text
        >
      </template>
      <v-alert v-else dense outlined shaped type="error">Unable to load line items.</v-alert>
    </vs-wrapper>

    <div v-else class="line-items">
      <v-skeleton-loader v-if="isLoading && lineItems === undefined" type="image" rounded />

      <template v-if="lineItems && lineItems.length === 0">
        <vs-text data-test="no-line-item-caption" type="caption">None </vs-text>
      </template>

      <div class="line-items-list">
        <template v-for="(lineItem, index) in lineItems">
          <v-divider v-if="index !== 0" :key="`divider-${lineItem.id}`" />

          <line-item-line
            :key="lineItem.id"
            :line-item="lineItem"
            :show-rates="hasLineItemRateAccess"
            :is-costed="ticketIsCosted"
            :is-signed="getLineItemSigned(lineItem.quantity, lineItem.name)"
          />
        </template>
      </div>

      <v-row v-if="shouldShowTotal">
        <v-col class="grow">
          <vs-text bold>Total</vs-text>
        </v-col>
        <v-col class="shrink">
          <vs-text bold data-test="line-items-total" truncate>{{ lineItemSubTotal }}</vs-text>
        </v-col>
      </v-row>
    </div>
  </vs-wrapper>
</template>

<script setup lang="ts">
import DisabledActionTooltip from '@/components/common/DisabledActionTooltip.vue'
import { usePermission, type PermissionResult } from '@/components/common/permission'
import { lineItemActionPermission } from './lineItemActionPermissions'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import { computed, watch } from 'vue'
import { getJobLineItemsQuery } from '@/api/jobs'
import VsText from '@/components/vision/VsText.vue'
import { useGetters, useStore } from 'vuex-composition-helpers'
import { useInvoiceQuery } from '@/api/invoice'
import { ApprovalStatus } from '@/models/job'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'
import { ItemWhenSigned } from '@/models/job-signature'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { useLineItemsTotal } from '@/components/jobs/line-items/useLineItemsTotal'
import LineItemLine from '@/components/jobs/line-items/LineItemLine.vue'
import { useJobSearchRouteContext } from '@/common/useJobSearchRouteContext'

interface Props {
  jobId: string
  jobApprovalStatus: ApprovalStatus
  assignedToServiceProviderId?: string | null
  hubId: string
  readonly?: boolean
  editPermission?: PermissionResult
  showActionButton?: boolean
  batchId?: string | null
  showBatchLink?: boolean
  lineItemsWhenSigned?: ItemWhenSigned[]
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  showActionButton: true,
  editPermission: undefined,
  batchId: undefined,
  lineItemsWhenSigned: undefined,
  showBatchLink: false,
  assignedToServiceProviderId: null,
})

const emits = defineEmits<{
  (e: 'line-item-count-changed', count: number): void
}>()
const batchIdRef = computed(() => props.batchId)
const { data: batch } = useInvoiceQuery(batchIdRef)
const { batchRouteName } = useJobSearchRouteContext()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const { currentOrgHasInvoicingEntitlement } = useBusinessUnitPermissions()

const {
  data: lineItems,
  isLoading,
  isError,
  error: lineItemError,
} = getJobLineItemsQuery(props.jobId)

watch(
  lineItems,
  (items) => {
    if (items !== undefined) {
      emits('line-item-count-changed', items.length)
    }
  },
  { immediate: true },
)

const formatter = useCurrencyFormatter()

const { hasLineItemEditApprovalAccess } = useGetters('permissions', [
  'hasLineItemEditApprovalAccess',
])

const store = useStore()

const hasLineItemRateAccessMethod = computed<
  (serviceProviderId: string | null, hubId: string) => boolean
>(() => store.getters['permissions/hasLineItemRateAccess'])
const hasLineItemRateAccess = computed(() =>
  hasLineItemRateAccessMethod.value(props.assignedToServiceProviderId, props.hubId),
)

const shouldShowTotal = computed(() => {
  const showNoItemsTotal =
    !props.showBatchLink || (props.showBatchLink && lineItems.value && lineItems.value.length !== 0)
  return hasLineItemRateAccess.value && showNoItemsTotal && ticketIsCosted.value
})

const { permission: lineItemPermission, allowed: canAddLineItems } = usePermission(() =>
  lineItemActionPermission({
    editPermission: props.editPermission,
    readonly: props.readonly,
    approvalRestricted:
      props.jobApprovalStatus === ApprovalStatus.APPROVED ||
      props.jobApprovalStatus === ApprovalStatus.PENDING_APPROVAL,
    hasApprovalAccess: hasLineItemEditApprovalAccess.value,
    isLoading: isLoading.value,
    isError: isError.value,
    errorStatus: lineItemError.value?.status,
  }),
)

const lineItemButtonIcon = computed(() => {
  if (lineItems.value && lineItems.value.length > 0) return ''
  return 'add'
})

const lineItemButtonLabel = computed(() => {
  if (lineItems.value && lineItems.value.length > 0) return 'Edit'
  return 'Add'
})

const lineItemSubTotal = computed(() => {
  if (!hasLineItemRateAccess.value) return ''
  return formatter.format(useLineItemsTotal(lineItems).value)
})

const ticketIsCosted = computed(() => {
  if (!lineItems.value) return false

  return lineItems.value.filter((lineItem) => lineItem.quantity && lineItem.rate).length > 0
})

function getLineItemSigned(quantity: number | null, name: string) {
  return props.lineItemsWhenSigned?.some((item) => item[name] === quantity)
}

function openLineItemBottomSheet() {
  if (!props.showActionButton || !canAddLineItems.value) return
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isLineItemBottomSheetVisible: true,
  })
}
</script>

<style scoped>
.line-items {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.line-items-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
}

span.material-symbols-outlined {
  font-size: var(--space-base);
}
span.material-symbols-outlined + span.material-symbols-outlined {
  margin-left: var(--space-smaller);
}
span.material-symbols-outlined.disabled {
  color: var(--color-grey--lighter);
}
span.material-symbols-outlined.enabled {
  color: var(--color-green--dark);
}

.icon-bg {
  background-color: white;
  border-radius: 50%;
  padding: 4px;
  margin-bottom: var(--space-smallest);
}

.heading-wrapper {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
}
</style>
